import React, { useState, useEffect, useMemo, useCallback } from "react"
import Layout from "@layouts/layout"
import { Intro, Block, Container, FilteredList, Loading } from "@components"
import axios from "axios"
import { graphql } from "gatsby"
import { HelmetDatoCms } from "gatsby-source-datocms"
import { getPricing } from "@context/productContext"
import { gtmPush } from "@helpers/gtmHelpers"
import ModuleArea from "../helpers/moduleArea"
import queryString from "query-string"

const Combined = ({ data, pageContext, location }) => {
  const {
    content,
    productsClearance,
    productsSpecials,
    globalConfig,
    brands
  } = data

  // Memoize the products object to prevent recreation on every render
  const products = useMemo(
    () => ({
      nodes: [...productsClearance.nodes, ...productsSpecials.nodes]
    }),
    [productsClearance.nodes, productsSpecials.nodes]
  )

  const isClearance = pageContext.title === "Clearance" ? true : false

  const getBrand = useCallback(
    brand => {
      return brands.nodes.find(({ name }) => name === brand)
    },
    [brands.nodes]
  )

  const sanitizeClearanceTag = useCallback(tag => {
    if (!tag || typeof tag !== "string") return ""
    return tag
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  }, [])

  // Memoize uniqueClearanceTags to prevent recalculation on every render
  const uniqueClearanceTags = useMemo(() => {
    const rawClearanceTags = products.nodes
      .map(product => product.clearanceTag)
      .filter(tag => tag && tag !== "")

    const normalizedTagMap = rawClearanceTags.reduce((acc, tag) => {
      const sanitizedTag = sanitizeClearanceTag(tag)
      if (!sanitizedTag) return acc

      const normalizedTag = sanitizedTag.toLowerCase()
      const existing = acc.get(normalizedTag) || []
      existing.push(tag)
      acc.set(normalizedTag, existing)
      return acc
    }, new Map())

    return [...normalizedTagMap.entries()]
      .map(([, tags]) => ({
        text: sanitizeClearanceTag(tags[0]),
        value: sanitizeClearanceTag(tags[0])
      }))
      .filter(tag => tag.text && tag.value) // Filter out any objects with null/undefined text or value
      .sort((a, b) => a.value.localeCompare(b.value)) // Sort alphabetically by value
  }, [products.nodes, sanitizeClearanceTag])

  const [availability, setAvailability] = useState({
    loading: true,
    isEmpty: products.nodes.length === 1,
    productStatuses: new Map(),
    errors: null
  })

  const params = queryString.parse(location.search)
  const initialLocationFilter = params.location?.trim() || ""
  const initialCategoryFilter = params.category || ""
  const initialClearanceTagFilter = sanitizeClearanceTag(params.tag || "")

  // Memoize defaultQueries to prevent recreation on every render
  const defaultQueries = useMemo(() => {
    const queries = new Map()
    if (initialLocationFilter) queries.set("state", initialLocationFilter)
    if (initialClearanceTagFilter)
      queries.set("clearanceTag", initialClearanceTagFilter)
    if (initialCategoryFilter) queries.set("category", initialCategoryFilter)
    return queries
  }, [initialLocationFilter, initialCategoryFilter, initialClearanceTagFilter])

  useEffect(() => {
    ;(async () => {
      // Only make API call if we actually have products to check
      if (!products.nodes || products.nodes.length === 0) {
        return
      }

      const specialsSkus = products.nodes
        .map(product => {
          const sku = product.sku || product.serial || product.id
          return sku
        })
        .filter(sku => sku) // Remove undefined/null SKUs

      const uniqueSpecialsSkus = [...new Set(specialsSkus)]

      let response = []

      try {
        response = await axios.post(
          "/.netlify/functions/stockAvailability",
          { skus: uniqueSpecialsSkus },
          {
            headers: {
              "Content-Type": "application/json"
            }
          }
        )
      } catch (err) {
        console.log("errors", err)
      }

      const newProductStatuses = new Map()

      if (response.data && Object.keys(response.data).length > 0) {
        Object.values(response.data).forEach(item => {
          newProductStatuses.set(item.sku, item.status)
        })
      }

      setAvailability(prev => ({
        loading: false,
        isEmpty: products.nodes.length < 2, // needs to be less than 2 because there is 1 hidden clearance item
        productStatuses: newProductStatuses,
        errors: null
      }))
    })()
  }, [productsClearance.nodes, productsSpecials.nodes]) // Only watch the source data

  const { loading, productStatuses, isEmpty, errors } = availability

  // Memoize clearanceItems to prevent recalculation on every render
  const clearanceItems = useMemo(() => {
    return products.nodes
      .map(product => {
        if (product?.warehouseData?.WarehouseState) {
          const warehouseState = product.warehouseData.WarehouseState.trim()
          product.location = warehouseState
          product.state = warehouseState
        }
        if (
          !product?.warehouseData?.WarehouseState &&
          product?.warehouseData?.WarehousePostcode
        ) {
          product.location = product.warehouseData.WarehousePostcode.trim()
        }
        if (
          product?.warehouseData?.WarehouseState &&
          product?.warehouseData?.WarehousePostcode
        ) {
          const warehouseState = product.warehouseData.WarehouseState.trim()
          const warehousePostcode = product.warehouseData.WarehousePostcode.trim()
          product.location = `${warehouseState}, ${warehousePostcode}`
        }

        const details = []

        if (product?.datoProduct?.specs.length > 0) {
          product.datoProduct.specs.map(spec => {
            details.push({
              name: spec.label,
              value:
                spec.value ||
                product[
                  spec.label
                    .trim()
                    .toLowerCase()
                    .replace(/ /gi, "-")
                ] ||
                product.specifics.find(specific => specific.name === spec.label)
                  ?.value ||
                null
            })
          })
        }

        if (
          details.findIndex(
            detail => detail.name.toLowerCase() === "serial"
          ) === -1
        ) {
          details.push({
            name: "Serial",
            value: product.serial
          })
        }
        if (product?.clearanceNote) {
          details.push({
            name: "Note",
            value: product.clearanceNote
          })
        }
        // if (product?.condition) {
        //   details.push({
        //     name: "Condition",
        //     value: product.condition
        //   })
        // }
        if (
          product.location &&
          details.findIndex(
            detail => detail.name.toLowerCase() === "location"
          ) === -1
        ) {
          details.unshift({
            name: "Location",
            value: product.location
          })
        }

        const transformedProduct = {
          title: product?.name,
          sku: product?.serial,
          category: product?.categories[0],
          condition: product?.condition,
          clearanceNote: product?.clearanceNote,
          clearanceUrl: product?.clearanceUrl,
          clearanceTag: sanitizeClearanceTag(product?.clearanceTag),
          clearancePscOffer: product?.clearancePscOffer,
          // description: product?.description,
          hover: true,
          elevated: true,
          padImage: true,
          imageDisclaimer: true,
          extended: true,
          special: true,
          hidden: product?.serial === "NO-CLEARANCE-ITEM" ? true : false,
          heading: product?.datoProduct?.name || product.name,
          brand: product?.datoProduct?.brand || product.brand,
          pricing: getPricing(product),
          id: product.id,
          state: product.state,
          dimensions:
            product?.datoProduct?.featuredSpecs.length > 0 &&
            product.datoProduct.featuredSpecs.map(featuredSpec => {
              return {
                icon: featuredSpec.icon,
                name: featuredSpec.label,
                label:
                  featuredSpec.value ||
                  product[
                    featuredSpec.label
                      .trim()
                      .toLowerCase()
                      .replace(/ /gi, "-")
                  ] ||
                  product.specifics.find(
                    specific => specific.name === featuredSpec.label
                  )?.value ||
                  null
              }
            }),
          details,
          image:
            product?.datoProduct?.image ||
            data?.globalConfig?.specialsImagePlaceholder,
          links: []
        }

        transformedProduct.links.push(
          {
            title: "Get a Quote",
            to: `/contact-us/specials-enquiry`,
            state: {
              heading: transformedProduct.heading,
              image: transformedProduct.image,
              brand: transformedProduct.brand,
              details: transformedProduct.details,
              price: transformedProduct?.pricing?.price,
              sku: transformedProduct.serial,
              isClearance: isClearance,
              clearanceUrl: transformedProduct.clearanceUrl,
              clearanceTag: transformedProduct.clearanceTag
            }
          },
          {
            title: "Get a Quote",
            to: `/contact-us/specials-enquiry`,
            state: {
              heading: transformedProduct.heading,
              image: transformedProduct.image,
              brand: transformedProduct.brand,
              details: transformedProduct.details,
              price: transformedProduct?.pricing?.price,
              sku: transformedProduct.serial,
              isClearance: isClearance,
              clearanceUrl: transformedProduct.clearanceUrl,
              clearanceTag: transformedProduct.clearanceTag
            }
          }
        )

        transformedProduct.isAvailable = productStatuses.get(
          transformedProduct.serial
        )

        return transformedProduct
      })
      .sort((a, b) => a?.pricing?.price - b?.pricing?.price)
  }, [
    products.nodes,
    productStatuses,
    isClearance,
    data?.globalConfig?.specialsImagePlaceholder,
    sanitizeClearanceTag
  ])

  // Memoize meta object to prevent recreation on every render
  const meta = useMemo(() => {
    const tags = []

    for (const [key, value] of Object.entries(globalConfig.metaTags)) {
      if (!value) continue

      if (key === "title") {
        tags.push(
          {
            tagName: key,
            content: value
          },
          {
            tagName: "meta",
            attributes: { property: `og:${key}`, content: value }
          },
          {
            tagName: "meta",
            attributes: { name: `twitter:${key}`, content: value }
          }
        )
      }

      if (key === "description") {
        tags.push(
          {
            tagName: "meta",
            attributes: { name: `${key}`, content: value }
          },
          {
            tagName: "meta",
            attributes: { property: `og:${key}`, content: value }
          },
          {
            tagName: "meta",
            attributes: { name: `twitter:${key}`, content: value }
          }
        )
      }

      if (key === "twitterCard") {
        tags.push({
          tagName: "meta",
          attributes: { name: `twitter:card`, content: value }
        })
      }

      if (key === "image") {
        tags.push(
          {
            tagName: "meta",
            attributes: { property: `og:${key}`, content: value.url }
          },
          {
            tagName: "meta",
            attributes: { name: `twitter:${key}`, content: value.url }
          }
        )
      }
    }

    return { tags }
  }, [globalConfig.metaTags])

  useEffect(() => {
    const ecommerceItem = []
    if (clearanceItems.length > 0) {
      clearanceItems.forEach(item => {
        ecommerceItem.push({
          item_id: item.sku,
          item_name: item.title,
          affiliation: "Spa World",
          item_brand: item.brand?.name || item.brand,
          item_category: item.category,
          item_variant: item.heading,
          item_list_name: pageContext.title,
          item_list_id: pageContext.title.toLowerCase().replaceAll(" ", "-"),
          price: item.pricing?.now,
          currency:
            pageContext.language === process.env.GATSBY_LANGUAGE
              ? "AUD"
              : "NZD",
          quantity: "1"
        })
      })

      gtmPush({ ecommerce: null })
      gtmPush({
        event: "view_item_list",
        ecommerce: {
          item_list_name: pageContext.title,
          item_list_id: pageContext.title.toLowerCase().replaceAll(" ", "-"),
          items: ecommerceItem
        }
      })
    }
  }, [clearanceItems, pageContext.title, pageContext.language])

  const ecommerceOnClick = useCallback(
    item => {
      const ecommerceItem = [
        {
          item_id: item.sku,
          item_name: item.title,
          affiliation: "Spa World",
          item_brand: item.brand?.name || item.brand,
          item_category: item.category,
          item_variant: item.heading,
          item_list_name: pageContext.title,
          item_list_id: pageContext.title.toLowerCase().replaceAll(" ", "-"),
          price: item.pricing?.now,
          currency:
            pageContext.language === process.env.GATSBY_LANGUAGE
              ? "AUD"
              : "NZD",
          quantity: "1"
        }
      ]

      gtmPush({ ecommerce: null })
      gtmPush({
        event: "select_item",
        ecommerce: {
          item_list_name: pageContext.title,
          item_list_id: pageContext.title.toLowerCase().replaceAll(" ", "-"),
          items: ecommerceItem
        }
      })
    },
    [pageContext.title, pageContext.language]
  )

  // Memoize filters to prevent recreation on every render
  const filters = useMemo(
    () =>
      [
        {
          name: "category",
          label: "type",
          placeholder: {
            text: "Select specials type"
          },
          options: [
            { text: "Certified Clearance", value: "Clearance" },
            { text: "Sale", value: "Specials" }
          ]
        },
        content.regions.length > 0 && {
          name: "state",
          label: "location",
          placeholder: {
            text: "Select your location"
          },
          options: content.regions.map(region => ({
            text: region.text?.trim() || region.text,
            value: region.value?.trim() || region.value
          }))
        },
        uniqueClearanceTags &&
          uniqueClearanceTags.length > 0 && {
            name: "clearanceTag",
            label: "category",
            placeholder: {
              text: "Select a category"
            },
            options: uniqueClearanceTags
          }
      ].filter(Boolean),
    [content.regions, uniqueClearanceTags]
  )

  return (
    <Layout>
      <HelmetDatoCms defer={false} seo={meta} />
      <Block gutters>
        <Intro
          breadcrumbs={pageContext.breadcrumbs}
          title={globalConfig.heading}
          lead={globalConfig.lead}
          showHeroImage={false}
          banner={globalConfig.banner ? globalConfig.banner : null}
        />
      </Block>
      <ModuleArea modules={globalConfig.modularContent} />
      <div className={"clearance-layout"}>
        <Container>
          {loading && !isEmpty && (
            <Block padding="double">
              <Loading />
            </Block>
          )}

          {isEmpty && (
            <Block padding="double">
              <center>Sorry, there are currently no products available.</center>
            </Block>
          )}

          {!loading && !isEmpty && (
            <FilteredList
              items={clearanceItems}
              filters={filters}
              type="product"
              defaultQueries={defaultQueries}
              location={location}
              onCardClick={ecommerceOnClick}
              itemsVisible={50}
              tiles={globalConfig.filterTiles}
            />
          )}
        </Container>
      </div>
    </Layout>
  )
}

export default Combined

export const query = graphql`
  query combinedProducts($language: String!) {
    productsSpecials: allNetoSpecial(
      filter: { categories: { eq: "Specials" } }
    ) {
      nodes {
        ...ClearanceProductCard
        datoProduct: datoSpecialProduct {
          name
          id
          slug
          country
          serial: sku
          featuredSpecs {
            value
            model {
              apiKey
            }
            __typename
            label
            id
            icon {
              alt
              url
              gatsbyImageData(
                layout: FULL_WIDTH
                imgixParams: {
                  h: "26"
                  fit: "crop"
                  w: "26"
                  q: 60
                  auto: "format"
                }
              )
            }
          }
          specs {
            value
            model {
              apiKey
            }
            __typename
            label
            id
          }
          image(locale: $language) {
            gatsbyImageData(
              layout: FULL_WIDTH
              imgixParams: {
                h: "330"
                fit: "fill"
                fill: "solid"
                w: "500"
                q: 60
                auto: "format"
              }
            )
            alt
          }
          brand(locale: $language) {
            name
            slug
            id
            logo {
              alt
              url
            }
          }
        }
      }
    }
    productsClearance: allNetoSpecial(
      filter: { categories: { eq: "Clearance" } }
    ) {
      nodes {
        ...ClearanceProductCard
        datoProduct: datoClearanceProduct {
          name
          id
          slug
          country
          serial: sku
          featuredSpecs {
            value
            model {
              apiKey
            }
            __typename
            label
            id
            icon {
              alt
              url
              gatsbyImageData(
                layout: FULL_WIDTH
                imgixParams: {
                  h: "26"
                  fit: "crop"
                  w: "26"
                  q: 60
                  auto: "format"
                }
              )
            }
          }
          specs {
            value
            model {
              apiKey
            }
            __typename
            label
            id
          }
          image(locale: $language) {
            gatsbyImageData(
              layout: FULL_WIDTH
              imgixParams: {
                h: "330"
                fit: "fill"
                fill: "solid"
                w: "500"
                q: 60
                auto: "format"
              }
            )
            alt
          }
          brand(locale: $language) {
            name
            slug
            id
            logo {
              alt
              url
            }
          }
        }
      }
    }

    content: contentYaml {
      regions {
        text
        value
      }
    }

    globalConfig: datoCmsGlobal(locale: $language) {
      filterTiles {
        id
        name
        image {
          gatsbyImageData(
            imgixParams: {
              w: "767"
              h: "704"
              fit: "crop"
              q: 60
              auto: "format"
            }
          )
        }
      }
      metaTags: combinedPageMetaTags {
        title
        description
        twitterCard
        image {
          url
        }
      }
      heading: combinedHeading
      lead: combinedLead
      modularContent: combined {
        ... on DatoCmsContentWithImage {
          ...ContentWithImage
        }
        ... on DatoCmsContent {
          ...Content
        }
      }
      banner: combinedBanner {
        id
        link {
          ...Link
        }
        image {
          url
          gatsbyImageData(
            layout: FULL_WIDTH
            imgixParams: {
              h: "295"
              w: "1170"
              fit: "crop"
              q: 60
              auto: "format"
            }
          )
          alt
        }
        mobileImage {
          url
          gatsbyImageData(
            layout: FULL_WIDTH
            imgixParams: { h: "600", fit: "cover", q: 60, auto: "format" }
          )
          alt
        }
      }
      specialsImagePlaceholder {
        gatsbyImageData(
          layout: FULL_WIDTH
          imgixParams: {
            h: "330"
            fit: "fill"
            fill: "solid"
            w: "500"
            auto: "format"
          }
        )
        alt
      }
    }

    brands: allDatoCmsBrand {
      nodes {
        ...BrandTag
      }
    }
  }
`
